import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import type {IPlayerProps} from '@lottiefiles/react-lottie-player';
import type {ReactNode} from 'react';
import {cloneElement, isValidElement, useState, useEffect} from 'react';

import {mergeSXProp} from '../../utils/merge-sx-prop';
import type {HTMLChakraProps, HeadingProps, StackProps, StyleProps, TextProps} from '../index';
import {Heading, Text, VStack, chakra} from '../index';

import {parts} from './theme/anatomy';
import {useStyles} from './theme/context';

export type FeedbackLayoutProps = Omit<StackProps, 'title'> & {
  title?: ReactNode | React.ReactElement<any>;
  description?: ReactNode;
  img?: ReactNode;
  body?: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  descriptionSx?: StyleProps;
};

export const FeedbackLayoutInternal = ({
  title,
  description,
  body,
  img,
  children,
  descriptionSx,
  ...rest
}: FeedbackLayoutProps) => {
  const isBodyVisible = title || description || body;

  return (
    <FeedbackLayoutContainer {...rest}>
      {img}
      {isBodyVisible && (
        <FeedbackLayoutBody>
          {title && typeof title === `string` ? (
            <FeedbackLayoutTitle>{title}</FeedbackLayoutTitle>
          ) : isValidElement(title) ? (
            cloneElement(title, {...parts.title.attrs})
          ) : (
            title
          )}
          {description && <FeedbackLayoutDescription sx={{...descriptionSx}}>{description}</FeedbackLayoutDescription>}
          {body}
        </FeedbackLayoutBody>
      )}
      {children}
    </FeedbackLayoutContainer>
  );
};

export const FeedbackLayoutContainer = ({sx, ...props}: StackProps) => {
  const {container: __css} = useStyles();
  return <ChakraDiv sx={mergeSXProp(__css, sx)} {...props} />;
};

export const FeedbackLayoutBody = ({sx, ...props}: StackProps) => {
  const {body: __css} = useStyles();
  return <VStack sx={mergeSXProp(__css, sx)} {...props} {...parts.body.attrs} />;
};

export const FeedbackLayoutTitle = ({sx, ...props}: HeadingProps) => {
  const {title} = useStyles();
  return <Heading size={`sm`} sx={mergeSXProp(title, sx)} {...props} {...parts.title.attrs} />;
};

export const FeedbackLayoutDescription = ({sx, ...props}: TextProps) => {
  const {description} = useStyles();
  return <Text as={`div`} size={`md`} variant={`secondary`} sx={mergeSXProp(description, sx)} {...props} />;
};

export const FeedbackLayoutPlayer = ({sx, ...props}: IPlayerProps & HTMLChakraProps<'div'>) => {
  const {player: __css} = useStyles();
  const [LottiePlayer, setLottiePlayer] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== `undefined`) {
      import(`@lottiefiles/react-lottie-player`).then(({Player}) => {
        const ChakraPlayer = chakra(Player);
        setLottiePlayer(() => ChakraPlayer);
      });
    }
  }, []);

  if (!LottiePlayer) {
    return null;
  }

  return (
    <LottiePlayer
      sx={mergeSXProp(__css, sx)}
      autoplay
      loop
      style={{width: `auto`, height: 300, overflow: `hidden`, margin: `auto`}}
      {...props}
    />
  );
};
